<template>
  <div class="baidu-aifanfan-page">
    <el-tabs v-model="activeTab" type="border-card" class="baidu-aifanfan-tabs">
      <el-tab-pane label="统计总览" name="overview">
        <section class="overview-panel">
          <div class="overview-hero">
            <div class="overview-hero__copy">
              <h2 class="overview-hero__title">爱番番线索总览</h2>
              <p class="overview-hero__desc">
                聚合百度爱番番线索推送到我方平台，待审核、审核、关联、回传状态，提高运营效率，优化智能客服投放策略。
              </p>
            </div>
            <div class="overview-hero__spotlight">
              <div class="overview-hero__spotlight-label">累计接收线索</div>
              <div class="overview-hero__spotlight-value">{{ overview.totalLeads }}</div>
              <div class="overview-hero__spotlight-note">
                <span>今日新增 {{ overview.todayLeads }}</span>
                <span
                  v-if="todayLeadTrend"
                  class="overview-hero__trend"
                  :class="`overview-hero__trend--${todayLeadTrend.direction}`"
                >
                  <span class="overview-hero__trend-text">
                    (较昨日 {{ todayLeadTrend.delta }}<i :class="todayLeadTrend.direction === 'up' ? 'el-icon-top' : 'el-icon-bottom'"></i>)
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div class="overview-grid">
            <article
              v-for="item in overviewCards"
              :key="item.key"
              class="overview-card"
              :class="[
                `overview-card--${item.tone}`,
                { 'overview-card--clickable': Boolean(item.reviewStatusTarget || item.action) }
              ]"
              @click="handleOverviewCardClick(item)"
            >
              <div class="overview-card__head">
                <span class="overview-card__label">{{ item.label }}</span>
                <span class="overview-card__chip">{{ item.chip }}</span>
              </div>
              <div class="overview-card__value">{{ overview[item.key] }}</div>
              <div class="overview-card__meta">{{ item.description }}</div>
            </article>
          </div>
        </section>
      </el-tab-pane>

      <el-tab-pane label="线索审核" name="review">
        <baidu-lead-review-list
          ref="leadReviewList"
          @refresh-overview="loadOverview"
        />
      </el-tab-pane>

      <el-tab-pane label="接入配置" name="access-config">
        <baidu-aifanfan-config mode="access" />
      </el-tab-pane>

      <el-tab-pane label="字段映射配置" name="field-config">
        <baidu-aifanfan-config mode="mapping" />
      </el-tab-pane>

      <el-tab-pane label="状态回传日志" name="callback">
        <baidu-callback-log @refresh-overview="loadOverview" />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { fetchBaiduAifanfanOverview } from '@/services/baiduAifanfanApi';
import BaiduLeadReviewList from './baidu-aifanfan/BaiduLeadReviewList.vue';
import BaiduAifanfanConfig from './baidu-aifanfan/BaiduAifanfanConfig.vue';
import BaiduCallbackLog from './baidu-aifanfan/BaiduCallbackLog.vue';

export default {
  name: 'BaiduAifanfan',
  components: {
    BaiduLeadReviewList,
    BaiduAifanfanConfig,
    BaiduCallbackLog
  },
  data() {
    return {
      activeTab: 'overview',
      overview: {
        totalLeads: 0,
        todayLeads: 0,
        yesterdayLeads: 0,
        waitingReview: 0,
        approvedReview: 0,
        rejectedReview: 0,
        linkedStudents: 0,
        callbackSuccess: 0
      },
      overviewCards: [
        {
          key: 'todayLeads',
          label: '今日接收',
          chip: '实时',
          description: '当天新进入系统的线索数量',
          tone: 'sky',
          action: 'openTodayLeads'
        },
        {
          key: 'waitingReview',
          label: '待审核',
          chip: '处理中',
          description: '仍需人工确认和跟进的线索',
          tone: 'amber',
          reviewStatusTarget: 'WAITING'
        },
        {
          key: 'approvedReview',
          label: '审核通过',
          chip: '有效',
          description: '已完成审核并继续进入后续流程',
          tone: 'emerald',
          reviewStatusTarget: 'APPROVED'
        },
        {
          key: 'rejectedReview',
          label: '审核拒绝',
          chip: '复核',
          description: '无效或需驳回处理的线索数量',
          tone: 'rose',
          reviewStatusTarget: 'REJECTED_INVALID'
        },
        {
          key: 'linkedStudents',
          label: '已关联学员',
          chip: '转化',
          description: '已成功关联到现有学员档案',
          tone: 'violet'
        },
        {
          key: 'callbackSuccess',
          label: '回传成功',
          chip: '同步',
          description: '状态回传百度成功的记录数量',
          tone: 'cyan'
        }
      ]
    };
  },
  created() {
    this.loadOverview();
    this.applyRouteQuery();
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteQuery();
      },
      deep: true
    }
  },
  computed: {
    todayLeadTrend() {
      const today = Number(this.overview.todayLeads) || 0;
      const yesterday = Number(this.overview.yesterdayLeads) || 0;
      if (today === yesterday) return null;
      return {
        direction: today > yesterday ? 'up' : 'down',
        delta: Math.abs(today - yesterday)
      };
    }
  },
  methods: {
    async loadOverview() {
      const res = await fetchBaiduAifanfanOverview();
      this.overview = { ...this.overview, ...(res.data || {}) };
    },
    applyRouteQuery() {
      const tab = String((this.$route.query && this.$route.query.tab) || '').trim();
      const keyword = String((this.$route.query && this.$route.query.keyword) || '').trim();
      if (tab !== 'review' && !keyword) return;
      this.activeTab = 'review';
      if (!keyword) return;
      this.$nextTick(() => {
        const reviewList = this.$refs.leadReviewList;
        if (reviewList && typeof reviewList.applyKeywordFilter === 'function') {
          reviewList.applyKeywordFilter(keyword);
        }
      });
    },
    handleOverviewCardClick(item) {
      if (!item.reviewStatusTarget && item.action !== 'openTodayLeads') return;
      this.activeTab = 'review';
      this.$nextTick(() => {
        const reviewList = this.$refs.leadReviewList;
        if (item.action === 'openTodayLeads' && reviewList && typeof reviewList.openTodayLeads === 'function') {
          reviewList.openTodayLeads();
          return;
        }
        if (reviewList && typeof reviewList.applyReviewStatusFilter === 'function') {
          reviewList.applyReviewStatusFilter(item.reviewStatusTarget);
        }
      });
    }
  }
};
</script>

<style scoped>
.baidu-aifanfan-page {
  min-height: 100%;
}

.baidu-aifanfan-tabs {
  min-height: 100%;
}

.baidu-aifanfan-page :deep(.el-tabs__content) {
  min-height: 100%;
}

.overview-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.overview-hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 26px;
  border-radius: 18px;
  background:
    radial-gradient(circle at right top, rgba(59, 130, 246, 0.14), transparent 34%),
    linear-gradient(135deg, #f8fbff 0%, #eef4ff 48%, #f7fbff 100%);
  border: 1px solid #d8e6ff;
  box-shadow: 0 14px 30px rgba(48, 88, 164, 0.08);
}

.overview-hero__copy {
  max-width: 620px;
}

.overview-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.10);
  color: #2b63cc;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.overview-hero__title {
  margin: 14px 0 10px;
  color: #172033;
  font-size: 28px;
  line-height: 1.25;
  font-weight: 700;
}

.overview-hero__desc {
  margin: 0;
  max-width: 60ch;
  color: #5d6980;
  font-size: 14px;
  line-height: 1.75;
}

.overview-hero__spotlight {
  min-width: 220px;
  padding: 20px 22px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(140, 180, 255, 0.35);
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

.overview-hero__spotlight-label {
  color: #66748d;
  font-size: 13px;
}

.overview-hero__spotlight-value {
  margin-top: 8px;
  color: #5866ff;
  font-size: 36px;
  line-height: 1.1;
  font-weight: 700;
}

.overview-hero__spotlight-note {
  margin-top: 10px;
  color: #9f00ff;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}

.overview-hero__trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #3d485b;
  font-size: 13px;
  font-weight: 500;
}

.overview-hero__trend-text {
  color: #3d485b;
}
.overview-hero__trend-text .el-icon-top{
  color: red;
}
.overview-hero__trend-text .el-icon-bottom{
  color: rgb(11, 210, 11);
}
.overview-hero__trend--up {
  color: #e34d59;
}

.overview-hero__trend--down {
  color: #2f9e63;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px;
}

.overview-card {
  position: relative;
  overflow: hidden;
  min-height: 156px;
  padding: 18px 18px 20px;
  border-radius: 16px;
  border: 1px solid #e6ebf5;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.overview-card::after {
  content: '';
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 4px;
  background: currentColor;
  opacity: 0.16;
}

.overview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
}

.overview-card--clickable {
  cursor: pointer;
}

.overview-card--clickable:hover {
  border-color: rgba(59, 130, 246, 0.24);
}

.overview-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.overview-card__label {
  color: #516078;
  font-size: 13px;
  font-weight: 600;
}

.overview-card__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(148, 163, 184, 0.18);
  color: currentColor;
  font-size: 12px;
  font-weight: 700;
}

.overview-card__value {
  margin-top: 24px;
  color: #152033;
  font-size: 34px;
  line-height: 1.05;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.overview-card__meta {
  margin-top: 14px;
  color: #748198;
  font-size: 12px;
  line-height: 1.65;
}

.overview-card--sky {
  color: #2f6fe4;
  background: linear-gradient(180deg, #f8fbff 0%, #f3f8ff 100%);
}

.overview-card--amber {
  color: #c67a1e;
  background: linear-gradient(180deg, #fffaf0 0%, #fff6e8 100%);
}

.overview-card--emerald {
  color: #1b9368;
  background: linear-gradient(180deg, #f4fffb 0%, #effcf7 100%);
}

.overview-card--rose {
  color: #d24d68;
  background: linear-gradient(180deg, #fff7f9 0%, #fff1f4 100%);
}

.overview-card--violet {
  color: #7357d6;
  background: linear-gradient(180deg, #f8f6ff 0%, #f4f1ff 100%);
}

.overview-card--cyan {
  color: #0f8c97;
  background: linear-gradient(180deg, #f2fdff 0%, #ebfbfd 100%);
}

@media (max-width: 1440px) {
  .overview-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .overview-hero {
    flex-direction: column;
  }

  .overview-hero__copy {
    max-width: none;
  }

  .overview-hero__spotlight {
    min-width: 0;
  }
}

@media (max-width: 768px) {
  .overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .overview-hero {
    padding: 20px;
  }

  .overview-hero__title {
    font-size: 24px;
  }
}

@media (max-width: 520px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }

  .overview-card {
    min-height: 140px;
  }
}
</style>
